import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Pagination, Table } from 'antd';
import { CheckOutlined, EditOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/lib/table';

import { useLazyConsultationsQuery } from 'app/services/edpApiV1/consultations';
import { useSubordinatesQuery } from 'app/services/edpApiV1/viewer';

import { dataForConsultationsTable, sortingDate } from 'shared/helpers/utils';
import { RequestsSearch, TConsultant } from 'shared/types/types';

import { usePullSort } from 'shared/hooks';
import { RolesUser, SortCondition } from 'shared/enums';
import { useIndicators } from 'features/indicators';
import { TConsultationsPreparedData } from 'shared/types/consultations.types';
import { SearchKeysNews } from 'shared/enums/news';
import SearchComponent from 'shared/components/search/news-faq';
import StringFormItem from 'shared/components/search/news-faq/stringFormItem/StringFormItem';
import NumberFormItem from 'shared/components/search/news-faq/numberFormItem/NumberFormItem';
import { getAllAvailableOrganizations } from 'entities/organizations/model/organizations';
import { useAppSelector } from 'app/store';
import { viewerModel } from 'entities/viewer';
import ModalConsultationsSubordinates from '../modalConsultationsSubordinates';
import style from './index.module.scss';

const ConsutationSubordinates = () => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [currentSize, setCurrentSize] = useState<number>(10);
  const [selectUser, setSelectUser] = useState<TConsultationsPreparedData[] | []>([]);
  const [dataConsultationsSubordinates, setDataConsultationsSubordinates] = useState<TConsultationsPreparedData[] | []>(
    [],
  );
  const [search, setSearch] = useState<RequestsSearch>({ byId: undefined, fio: undefined });
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [requestId, setRequestId] = useState<number>();
  const [showDetails, setShowDetails] = useState<boolean>(false);
  const [consultantIdCurrent, setConsultantIdCurrent] = useState<number>();
  const [sortInfo, sortTrigger] = usePullSort();
  const navigate = useNavigate();
  const sortOption = sortInfo.value && sortInfo.value === SortCondition.ASCEND ? SortCondition.ASC : SortCondition.DESC;
  const allAvailableOrganizations = useAppSelector(getAllAvailableOrganizations);
  const { role } = useAppSelector(viewerModel.me.selectors.selectViewerProfile);

  const [getConsultations, { data, isSuccess, isFetching }] = useLazyConsultationsQuery();

  useEffect(() => {
    getConsultations({
      type: 'subordinatesInWork',
      size: currentSize,
      page: currentPage,
      order: sortInfo.value ? sortOption : undefined,
      allAvailableOrganizations,
      ...search,
    });
  }, [currentPage, currentSize, getConsultations, search, sortInfo.value, sortOption, allAvailableOrganizations]);

  const { data: allSubordinates } = useSubordinatesQuery({ include: 'me' });

  const handleChangeResponsible = (id: number, consultantId: number): void => {
    setRequestId(id);
    setOpenModal(true);
    setConsultantIdCurrent(consultantId);
  };

  const columns: ColumnsType<TConsultationsPreparedData> = [
    {
      title: '№',
      dataIndex: 'id',
      key: 'key',
      width: 140,
    },
    {
      title: 'Дата',
      dataIndex: 'created',
      key: 'created',
      sortDirections: [SortCondition.ASCEND, SortCondition.DESCEND],
      sorter: (a, b) => sortingDate(a.created, b.created),
      onHeaderCell: () => ({
        onClick: () => sortTrigger(),
      }),
    },
    {
      title: 'Тема',
      dataIndex: 'topic',
      key: 'topic',
    },
    {
      title: 'ФИО',
      dataIndex: 'fio',
      key: 'fio',
    },
    {
      title: 'Должность',
      dataIndex: 'position',
      key: 'position',
    },
    {
      title: 'Подразделение',
      dataIndex: 'division',
      key: 'division',
    },
    {
      title: 'Дан ответ',
      dataIndex: 'isHRReplied',
      key: 'isHRReplied',
      width: 102,
      render: (isHRReplied: boolean) => {
        if (isHRReplied) {
          return <CheckOutlined style={{ color: 'green', fontSize: '20px', width: '100%', textAlign: 'center' }} />;
        }
      },
    },
  ];

  if (!allAvailableOrganizations && role === RolesUser.HRDirector) {
    columns.push({
      title: 'Ответственный',
      dataIndex: 'consultant',
      key: 'consultant',
      render: (consultant: TConsultant, record: { key: React.Key }) => (
        <>
          {consultant.name || consultant.middleName || consultant.lastName ? (
            <div className={style.editConsultant}>
              <span>{`${consultant.lastName} ${consultant.name} ${consultant.middleName ?? ''}`}</span>
              <EditOutlined
                className={style.btnChangeResponsible}
                onClick={() => handleChangeResponsible(Number(record.key), consultant.id)}
              />
            </div>
          ) : (
            <span
              className={style.changeResponsible}
              onClick={() => handleChangeResponsible(Number(record.key), consultant.id)}
            >
              Назначить
            </span>
          )}
        </>
      ),
    });
  }

  const handlePagination = (value: number, pageSize: number) => {
    setCurrentSize(pageSize);
    setCurrentPage(value);
  };
  const rowSelection = {
    onChange: (selectedRowKeys: React.Key[], selectedRows: TConsultationsPreparedData[]) => {
      setRequestId(Number(selectedRowKeys));
      setSelectUser(selectedRows);
      setShowDetails(true);
    },
  };

  useEffect(() => {
    if (data?.data) {
      const dataTable = dataForConsultationsTable(data.data);
      if (dataTable) {
        setDataConsultationsSubordinates(dataTable);
      }
    }
  }, [data]);

  useIndicators(isSuccess, isFetching);

  const onSearch = (data: string, key: SearchKeysNews) => {
    if (key === SearchKeysNews.SEARCH_ID) {
      setSearch({ fio: undefined, byId: data ? data : undefined });
    } else if (key === SearchKeysNews.SEARCH_NAME) {
      setSearch({ byId: undefined, fio: data ? data : undefined });
    } else {
      setSearch({ [key]: data ? data : undefined });
    }
  };
  return (
    <>
      <SearchComponent
        selectOptions={{
          [SearchKeysNews.SEARCH_NAME]: {
            name: SearchKeysNews.SEARCH_NAME,
            alias: 'ФИО',
            node: StringFormItem({
              name: SearchKeysNews.SEARCH_NAME,
              placeholder: 'Введите ФИО',
              onSearch: data => {
                onSearch?.(data, SearchKeysNews.SEARCH_NAME);
              },
            }),
          },
          [SearchKeysNews.SEARCH_ID]: {
            name: SearchKeysNews.SEARCH_ID,
            alias: 'Номер заявки',
            node: NumberFormItem({
              name: SearchKeysNews.SEARCH_ID,
              placeholder: 'Введите номер заявки',
              onSearch: data => {
                onSearch?.(data, SearchKeysNews.SEARCH_ID);
              },
            }),
          },
          [SearchKeysNews.PERSONNEL_NUMBER]: {
            name: SearchKeysNews.PERSONNEL_NUMBER,
            alias: 'ТН',
            node: StringFormItem({
              name: SearchKeysNews.PERSONNEL_NUMBER,
              placeholder: 'Введите ТН',
              onSearch: data => {
                onSearch?.(data, SearchKeysNews.PERSONNEL_NUMBER);
              },
            }),
          },
          [SearchKeysNews.POSITION_NAME]: {
            name: SearchKeysNews.POSITION_NAME,
            alias: 'Должность',
            node: StringFormItem({
              name: SearchKeysNews.POSITION_NAME,
              placeholder: 'Введите должность',
              onSearch: data => {
                onSearch?.(data, SearchKeysNews.POSITION_NAME);
              },
            }),
          },
          [SearchKeysNews.DIVISION_NAME]: {
            name: SearchKeysNews.DIVISION_NAME,
            alias: 'Подразделение',
            node: StringFormItem({
              name: SearchKeysNews.DIVISION_NAME,
              placeholder: 'Введите подразделение',
              onSearch: data => {
                onSearch?.(data, SearchKeysNews.DIVISION_NAME);
              },
            }),
          },
        }}
      />
      {showDetails && requestId && selectUser && (
        <div className={style.subordinationBtnMore}>
          <Button type="default" onClick={() => navigate(`/consultations/user/subordination/${requestId}`)}>
            Подробнее
          </Button>
        </div>
      )}
      <Table
        scroll={{ x: true }}
        rowSelection={{
          type: 'radio',
          ...rowSelection,
        }}
        dataSource={dataConsultationsSubordinates}
        columns={columns}
        pagination={false}
        bordered
        rowClassName={record => {
          if (record.downtimeStatus === 'red') {
            return style.red;
          } else if (record.downtimeStatus === 'yellow') {
            return style.yellow;
          }
          return style.blue;
        }}
      />

      <div className={style.paginationWrap}>
        <Pagination
          defaultCurrent={currentSize}
          current={currentPage}
          pageSize={currentSize}
          showSizeChanger
          total={data?.pagination?.total}
          onChange={(value: number, pageSize: number) => handlePagination(value, pageSize)}
        />
      </div>
      {allSubordinates && allSubordinates.length > 0 && requestId && consultantIdCurrent && (
        <ModalConsultationsSubordinates
          allSubordinates={allSubordinates}
          openModal={openModal}
          setOpenModal={setOpenModal}
          requestId={requestId}
          consultantIdCurrent={consultantIdCurrent}
        />
      )}
    </>
  );
};

export default ConsutationSubordinates;
